import {
	AccountCircle,
	AdminPanelSettings,
	AlternateEmail,
	Badge,
	Business,
	Camera,
	CheckCircle,
	Close,
	Edit,
	EventBusy,
	Info,
	Lock,
	LockReset,
	Person,
	Save,
	Schedule,
	Visibility,
	VisibilityOff,
	Work
} from '@mui/icons-material';
import {
	Avatar,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	TextField,
	Typography,
	alpha,
	styled
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { MuiTelInput } from 'mui-tel-input';
import React, { useContext, useEffect, useState } from "react";
import avatarPng from "../../assets/Profile-Avatar.png";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import ToastSuccess from "../../toast/success/toastSuccess";
import { i18n } from "../../translate/i18n";
import { detectCountryFromNumber } from "../../utils/phoneUtils";
import QueueSelect from "../QueueSelect";

// Constantes para o Menu de seleção
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

// Componentes estilizados
const UserAvatar = styled(Avatar)(({ theme }) => ({
	width: 100,
	height: 100,
	border: `3px solid ${theme.palette.primary.main}`,
	boxShadow: theme.shadows[3],
	margin: '0 auto 8px auto',
	position: 'relative',
	transition: 'all 0.3s ease',
	'&:hover': {
		transform: 'scale(1.05)',
	}
}));

const AvatarEditButton = styled(IconButton)(({ theme }) => ({
	position: 'absolute',
	bottom: 0,
	right: 0,
	backgroundColor: theme.palette.primary.main,
	color: theme.palette.common.white,
	padding: theme.spacing(0.5),
	'&:hover': {
		backgroundColor: theme.palette.primary.dark,
	},
	boxShadow: theme.shadows[2],
}));

const SectionIcon = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	marginRight: theme.spacing(1),
	color: theme.palette.primary.main,
}));

const SectionHeader = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	marginBottom: theme.spacing(2),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(2),
	backgroundColor: alpha(theme.palette.background.paper, 0.6),
	borderRadius: theme.shape.borderRadius,
	boxShadow: 'none',
	border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
	transition: 'all 0.2s ease',
	'&:hover': {
		boxShadow: theme.shadows[2],
		borderColor: alpha(theme.palette.primary.main, 0.2),
	}
}));

const LoadingButton = styled(Button)(({ theme }) => ({
	position: 'relative',
}));

const ButtonProgress = styled(CircularProgress)(({ theme }) => ({
	position: 'absolute',
	top: '50%',
	left: '50%',
	marginTop: -12,
	marginLeft: -12,
}));

const CreateOrEditUserModal = ({ open, onClose, userId }) => {
	const { user: loggedInUser } = useContext(AuthContext);

	// Estados para formulário
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [number, setNumber] = useState(""); // Número completo com DDI para salvar
	const [displayNumber, setDisplayNumber] = useState(""); // Número sem DDI para exibir no input
	const [phoneCountry, setPhoneCountry] = useState("BR");
	const [password, setPassword] = useState("");
	const [oldPassword, setOldPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [profile, setProfile] = useState("user");
	const [position, setPosition] = useState("");
	const [companyId, setCompanyId] = useState(loggedInUser.companyId);
	const [unideskId, setUnideskId] = useState("");
	const [periodWork, setPeriodWork] = useState([]);
	const [availablePeriods, setAvailablePeriods] = useState([
		{ key: 'morning', label: 'Manhã', times: '', color: 'orange' },
		{ key: 'afternoon', label: 'Tarde', times: '', color: 'gold' },
		{ key: 'night', label: 'Noite', times: '', color: 'darkblue' },
	]);
	const [userImage, setUserImage] = useState(null);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [companies, setCompanies] = useState([]);
	const [selectedDaysOff, setSelectedDaysOff] = useState([]);
	const [extraDaysOff, setExtraDaysOff] = useState([]);
	const [extraDayInput, setExtraDayInput] = useState(null);
	const [noQueue, setNoQueue] = useState(true);
	const [changePassword, setChangePassword] = useState(false);
	const [isUnidesk, setIsUnidesk] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Estados para validação
	const [errors, setErrors] = useState({});
	const [touched, setTouched] = useState({});

	// Função para validar campos
	const validateField = (fieldName, value) => {
		let error = "";

		switch (fieldName) {
			case "name":
				if (!value) error = "Obrigatório";
				else if (value.length < 2) error = "Muito Curto(a)!";
				else if (value.length > 50) error = "Muito Longo(a)!";
				break;
			case "email":
				if (!value) error = "Obrigatório";
				else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value))
					error = "Email Inválido";
				break;
			case "password":
				if (changePassword || !userId) {
					if (!value) error = "Obrigatório";
					else if (value.length < 5) error = "Muito Curto(a)!";
				}
				break;
			case "oldPassword":
				if (changePassword && !value) error = "Obrigatório";
				break;
			case "confirmPassword":
				if (changePassword) {
					if (!value) error = "Obrigatório";
					else if (value !== password) error = "As senhas não são iguais!";
				}
				break;
			default:
				break;
		}

		return error;
	};

	// Função para lidar com mudanças nos campos
	const handleChange = (fieldName, value) => {
		// Atualizar valor
		switch (fieldName) {
			case "name": setName(value); break;
			case "email": setEmail(value); break;
			case "number":
				// MuiTelInput já retorna o número no formato +55 11 96299-5342
				// Vamos extrair apenas os dígitos
				const cleanValue = value.replace(/[\s\-\(\)]/g, '').replace(/^\+/, '');

				// Atualizar número exibido
				setDisplayNumber(value);

				// Detectar país
				const detectedCountry = detectCountryFromNumber(value);
				setPhoneCountry(detectedCountry);

				// Salvar apenas os dígitos (sem + e sem espaços) - ex: 5511962995342
				setNumber(cleanValue);
				break;
			case "password": setPassword(value); break;
			case "oldPassword": setOldPassword(value); break;
			case "confirmPassword": setConfirmPassword(value); break;
			case "profile": setProfile(value); break;
			case "position": setPosition(value); break;
			case "companyId": setCompanyId(value); break;
			case "unideskId": setUnideskId(value); break;
			default: break;
		}

		// Marcar como tocado
		setTouched(prev => ({ ...prev, [fieldName]: true }));

		// Validar e atualizar erros
		const fieldError = validateField(fieldName, value);
		setErrors(prev => ({ ...prev, [fieldName]: fieldError }));
	};

	// Função para lidar com a mudança do período de trabalho
	const handleChangePeriodWork = (event) => {
		const {
			target: { value },
		} = event;
		setPeriodWork(
			typeof value === 'string' ? value.split(',') : value,
		);
	};

	// Função para traduzir períodos
	const translatePeriods = (periods) => {
		return periods.map(period => {
			if (period === 'morning') return 'Manhã';
			if (period === 'afternoon') return 'Tarde';
			if (period === 'night') return 'Noite';
			return period;
		});
	};

	// Dias da semana para folgas fixas
	const weekDays = [
		{ value: 0, label: 'Domingo' },
		{ value: 1, label: 'Segunda' },
		{ value: 2, label: 'Terça' },
		{ value: 3, label: 'Quarta' },
		{ value: 4, label: 'Quinta' },
		{ value: 5, label: 'Sexta' },
		{ value: 6, label: 'Sábado' },
	];

	// Função para adicionar folga avulsa
	const handleAddExtraDay = (date) => {
		if (!date || !date.isValid()) return;
		const formatted = date.format('YYYY-MM-DD');
		if (!extraDaysOff.includes(formatted)) {
			setExtraDaysOff(prev => [...prev, formatted]);
		}
		setExtraDayInput(null);
	};

	// Função para remover folga avulsa
	const handleRemoveExtraDay = (dateToRemove) => {
		setExtraDaysOff(prev => prev.filter(d => d !== dateToRemove));
	};

	// Função para verificar a imagem do usuário
	const getUserImage = () => {
		// Se tiver uma nova imagem selecionada (array de File objects)
		if (userImage?.length && userImage[0]?.name) {
			return URL.createObjectURL(userImage[0]);
		}

		// Se userImage é um objeto com medias.mediaUrl (vindo do backend)
		if (userImage && typeof userImage === 'object' && userImage.medias?.mediaUrl) {
			return userImage.medias.mediaUrl;
		}

		// Fallback para URL direta (string) - ex: http://localhost/public/...
		if (userImage && typeof userImage === 'string' && userImage.startsWith('http')) {
			return userImage;
		}

		// Se não tiver nada, retornar avatar padrão
		return avatarPng;
	};

	// Função para processar upload de imagem
	const handleImageUpload = (e) => {
		if (!e.target.files) return;
		setUserImage(Array.from(e.target.files));
	};

	// Estado para mostrar/esconder senhas
	const [showPassword, setShowPassword] = useState(false);
	const [showOldPassword, setShowOldPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	// Função para alternar visibilidade da senha
	const togglePasswordVisibility = (field) => {
		switch (field) {
			case 'password':
				setShowPassword(!showPassword);
				break;
			case 'oldPassword':
				setShowOldPassword(!showOldPassword);
				break;
			case 'confirmPassword':
				setShowConfirmPassword(!showConfirmPassword);
				break;
			default:
				break;
		}
	};

	// Efeito para carregar períodos de trabalho configurados
	useEffect(() => {
		const fetchPeriods = async () => {
			try {
				const { data } = await api.get('/settings-company');
				const pow = data?.findPeriodOfWork;
				if (pow) {
					const periods = [];
					const periodConfig = [
						{ key: 'morning', label: 'Manhã', color: 'orange' },
						{ key: 'afternoon', label: 'Tarde', color: 'gold' },
						{ key: 'night', label: 'Noite', color: 'darkblue' },
					];
					for (const cfg of periodConfig) {
						if (pow[cfg.key]) {
							try {
								const parsed = JSON.parse(pow[cfg.key]);
								const hasValidTimes = parsed.start && parsed.end
									&& parsed.start !== '00:00' && parsed.end !== '00:00';
								periods.push({
									...cfg,
									times: hasValidTimes ? `${parsed.start} - ${parsed.end}` : '',
								});
							} catch {
								periods.push({ ...cfg, times: '' });
							}
						} else {
							periods.push({ ...cfg, times: '' });
						}
					}
					setAvailablePeriods(periods);
				}
			} catch {
				// Mantém os períodos padrão definidos no state inicial
			}
		};

		if (open) {
			fetchPeriods();
		}
	}, [open]);

	// Efeito para carregar dados do usuário e empresas
	useEffect(() => {
		const fetchUser = async () => {
			setChangePassword(false);
			setUserImage("");

			if (!userId) return;

			try {
				const { data } = await api.get(`/users/${userId}`);

				if (data?.period) {
					setPeriodWork(JSON.parse(data.period));
				}

				setName(data.name || "");
				setEmail(data.email || "");
				const phoneNum = data.number || "";
				setNumber(phoneNum);
				if (phoneNum) {
					const country = detectCountryFromNumber(phoneNum);
					setPhoneCountry(country);
					let formattedNumber = phoneNum;
					if (!phoneNum.startsWith('+')) {
						formattedNumber = '+' + phoneNum;
					}
					setDisplayNumber(formattedNumber);
				} else {
					setDisplayNumber("");
				}
				setProfile(data.profile || "user");
				setPosition(data.position === 'null' ? null : data?.position || "");
				setCompanyId(data.companyId || loggedInUser.companyId);
				setUnideskId(data.unideskId || "");
				setUserImage(data);
				setNoQueue(data.noQueue);

				try { setSelectedDaysOff(data?.daysOff ? JSON.parse(data.daysOff) : []); } catch { setSelectedDaysOff([]); }
				try { setExtraDaysOff(data?.extraDaysOff ? JSON.parse(data.extraDaysOff) : []); } catch { setExtraDaysOff([]); }

				const userQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(userQueueIds);
			} catch (err) {
				ToastError(err);
			}
		};

		const fetchCompanies = async () => {
			if (!loggedInUser?.id) return;

			try {
				const { data } = await api.get("/company");
				setCompanies(data);
			} catch (err) {
				ToastError(err);
			}
		};

		fetchCompanies();
		fetchUser();
	}, [userId, open, loggedInUser]);

	// Função para limpar e fechar o modal
	const handleClose = () => {
		onClose();
		resetForm();
	};

	// Função para resetar o formulário
	const resetForm = () => {
		setName("");
		setEmail("");
		setNumber("");
		setDisplayNumber("");
		setPhoneCountry("BR");
		setPassword("");
		setOldPassword("");
		setConfirmPassword("");
		setProfile("user");
		setPosition("");
		setCompanyId(loggedInUser.companyId);
		setUnideskId("");
		setPeriodWork([]);
		setSelectedDaysOff([]);
		setExtraDaysOff([]);
		setExtraDayInput(null);
		setNoQueue(true);
		setUserImage(null);
		setSelectedQueueIds([]);
		setChangePassword(false);
		setErrors({});
		setTouched({});
	};

	// Função para validar o formulário completo
	const validateForm = () => {
		const newErrors = {
			name: validateField("name", name),
			email: validateField("email", email),
			password: validateField("password", password),
			oldPassword: validateField("oldPassword", oldPassword),
			confirmPassword: validateField("confirmPassword", confirmPassword),
		};

		setErrors(newErrors);

		// Marcar campos relevantes como tocados
		setTouched({
			name: true,
			email: true,
			password: changePassword || !userId,
			oldPassword: changePassword,
			confirmPassword: changePassword,
		});

		// Verificar se há erros
		return !Object.values(newErrors).some(error => error !== "");
	};

	// Função para salvar o usuário
	const handleSaveUser = async () => {
		if (!validateForm()) return;

		setIsSubmitting(true);

		const userData = new FormData();
		userData.append("name", name);
		userData.append("email", email);
		userData.append("number", number);
		userData.append("password", password);
		userData.append("profile", profile);
		userData.append("position", position);
		userData.append("period", JSON.stringify(periodWork));
		userData.append("companyId", companyId);
		userData.append("queueIds", JSON.stringify(selectedQueueIds));
		userData.append("noQueue", noQueue);
		userData.append("daysOff", JSON.stringify(selectedDaysOff));
		userData.append("extraDaysOff", JSON.stringify(extraDaysOff));

		if (userId) {
			userData.append("id", userId);
			userData.append("oldPassword", oldPassword);
		}

		if (unideskId) {
			userData.append("unideskId", unideskId);
		}

		if (userImage?.length > 0 && userImage[0] !== 'h') {
			userData.append("media", userImage[0]);
		}

		try {
			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				await api.post("/users", userData);
			}

			ToastSuccess(i18n.t("userModal.success"));
			handleClose();
		} catch (err) {
			ToastError(err);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			scroll="paper"
			BackdropProps={{
				style: {
					backdropFilter: 'blur(5px)',
					backgroundColor: 'rgba(0, 0, 30, 0.4)',
				},
			}}
		>
			<DialogTitle sx={{ px: 3, py: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					{userId ? <Edit color="primary" sx={{ mr: 1 }} /> : <Person color="primary" sx={{ mr: 1 }} />}
					<Typography variant="h6" fontWeight="bold">
						{userId
							? i18n.t("userModal.title.edit")
							: i18n.t("userModal.title.add")}
					</Typography>
				</Box>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ px: 3, py: 2 }}>
				<Grid container spacing={2}>
					{/* Seção de Avatar */}
					<Grid item xs={12} sx={{ textAlign: 'center', mb: 1 }}>
						<Box sx={{ position: 'relative', display: 'inline-block' }}>
							<UserAvatar
								src={getUserImage()}
								alt={name || "user"}
							/>
							<AvatarEditButton
								component="label"
								size="small"
							>
								<Camera fontSize="small" />
								<input
									hidden
									onChange={handleImageUpload}
									id="userImage"
									accept="image/*"
									type="file"
								/>
							</AvatarEditButton>
						</Box>
					</Grid>

					{/* Informações Básicas */}
					<Grid item xs={12}>
						<StyledPaper>
							<SectionHeader>
								<SectionIcon><AccountCircle /></SectionIcon>
								<Typography variant="subtitle1" fontWeight="bold">
									Informações Básicas
								</Typography>
							</SectionHeader>

							<Grid container spacing={2}>
								<Grid item xs={12} md={6}>
									<TextField
										label={i18n.t("userModal.form.name")}
										value={name}
										onChange={(e) => handleChange("name", e.target.value)}
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										size="small"
										fullWidth
										autoFocus
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<Person fontSize="small" color="action" />
												</InputAdornment>
											),
										}}
									/>
								</Grid>

								<Grid item xs={12} md={6}>
									<MuiTelInput
										label="Número do Usuário (Opcional)"
										value={displayNumber}
										onChange={(value) => handleChange("number", value)}
										variant="outlined"
										size="small"
										fullWidth
										defaultCountry={phoneCountry}
										forceCallingCode
									/>
								</Grid>

								<Grid item xs={12} md={12}>
									<TextField
										label={i18n.t("userModal.form.email")}
										value={email}
										onChange={(e) => handleChange("email", e.target.value)}
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										size="small"
										fullWidth
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<AlternateEmail fontSize="small" color="action" />
												</InputAdornment>
											),
										}}
									/>
								</Grid>

							</Grid>
						</StyledPaper>
					</Grid>

					{/* Seção de Senha */}
					<Grid item xs={12}>
						<StyledPaper>
							<SectionHeader>
								<SectionIcon><Lock /></SectionIcon>
								<Typography variant="subtitle1" fontWeight="bold">
									Autenticação
								</Typography>
							</SectionHeader>

							{/* Opção para mudar senha (apenas na edição) */}
							{userId && loggedInUser.profile !== 'admin' && (
								<FormControlLabel
									control={
										<Checkbox
											checked={changePassword}
											onChange={(e) => {
												setChangePassword(e.target.checked);
												if (!e.target.checked) {
													setPassword("");
													setOldPassword("");
													setConfirmPassword("");
												}
											}}
											size="small"
											color="primary"
										/>
									}
									label={
										<Box sx={{ display: 'flex', alignItems: 'center' }}>
											<LockReset fontSize="small" sx={{ mr: 0.5 }} />
											<Typography variant="body2">{i18n.t("userModal.form.changePassword")}</Typography>
										</Box>
									}
									sx={{ mb: 1 }}
								/>
							)}

							<Grid container spacing={2}>
								{/* Campos de senha para edição */}
								{changePassword && userId && loggedInUser.profile !== 'admin' && (
									<>
										<Grid item xs={12}>
											<TextField
												label={i18n.t("userModal.form.oldPassword")}
												type={showOldPassword ? "text" : "password"}
												value={oldPassword}
												onChange={(e) => handleChange("oldPassword", e.target.value)}
												error={touched.oldPassword && Boolean(errors.oldPassword)}
												helperText={touched.oldPassword && errors.oldPassword}
												variant="outlined"
												size="small"
												fullWidth
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<Lock fontSize="small" color="action" />
														</InputAdornment>
													),
													endAdornment: (
														<InputAdornment position="end">
															<IconButton
																size="small"
																onClick={() => togglePasswordVisibility('oldPassword')}
																edge="end"
															>
																{showOldPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
															</IconButton>
														</InputAdornment>
													),
												}}
											/>
										</Grid>

										<Grid item xs={12} md={6}>
											<TextField
												label={i18n.t("userModal.form.newPassword")}
												type={showPassword ? "text" : "password"}
												value={password}
												onChange={(e) => handleChange("password", e.target.value)}
												error={touched.password && Boolean(errors.password)}
												helperText={touched.password && errors.password}
												variant="outlined"
												size="small"
												fullWidth
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<Lock fontSize="small" color="action" />
														</InputAdornment>
													),
													endAdornment: (
														<InputAdornment position="end">
															<IconButton
																size="small"
																onClick={() => togglePasswordVisibility('password')}
																edge="end"
															>
																{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
															</IconButton>
														</InputAdornment>
													),
												}}
											/>
										</Grid>

										<Grid item xs={12} md={6}>
											<TextField
												label={i18n.t("userModal.form.confirmPassword")}
												type={showConfirmPassword ? "text" : "password"}
												value={confirmPassword}
												onChange={(e) => handleChange("confirmPassword", e.target.value)}
												error={touched.confirmPassword && Boolean(errors.confirmPassword)}
												helperText={touched.confirmPassword && errors.confirmPassword}
												variant="outlined"
												size="small"
												fullWidth
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<Lock fontSize="small" color="action" />
														</InputAdornment>
													),
													endAdornment: (
														<InputAdornment position="end">
															<IconButton
																size="small"
																onClick={() => togglePasswordVisibility('confirmPassword')}
																edge="end"
															>
																{showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
															</IconButton>
														</InputAdornment>
													),
												}}
											/>
										</Grid>
									</>
								)}

								{/* Campo de senha para novo usuário ou admin */}
								{(userId === null || loggedInUser.profile === 'admin') && (
									<Grid item xs={12}>
										<TextField
											label={i18n.t("userModal.form.password")}
											type={showPassword ? "text" : "password"}
											value={password}
											onChange={(e) => handleChange("password", e.target.value)}
											error={touched.password && Boolean(errors.password)}
											helperText={touched.password && errors.password}
											variant="outlined"
											size="small"
											fullWidth
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<Lock fontSize="small" color="action" />
													</InputAdornment>
												),
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															size="small"
															onClick={() => togglePasswordVisibility('password')}
															edge="end"
														>
															{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
														</IconButton>
													</InputAdornment>
												),
											}}
										/>
									</Grid>
								)}
							</Grid>
						</StyledPaper>
					</Grid>

					{/* Seção de Detalhes do Perfil */}
					{(loggedInUser.profile !== 'adminSuper' && loggedInUser.profile !== 'user') && (
						<Grid item xs={12}>
							<StyledPaper>
								<SectionHeader>
									<SectionIcon><Badge /></SectionIcon>
									<Typography variant="subtitle1" fontWeight="bold">
										Detalhes do Perfil
									</Typography>
								</SectionHeader>

								<Grid container spacing={2}>
									<Grid item xs={12} md={6}>
										<FormControl
											fullWidth
											variant="outlined"
											size="small"
										>
											<InputLabel id="profile-selection-label">
												{i18n.t("userModal.form.profile")}
											</InputLabel>
											<Select
												labelId="profile-selection-label"
												id="profile-selection"
												value={profile}
												onChange={(e) => setProfile(e.target.value)}
												label={i18n.t("userModal.form.profile")}
												disabled={loggedInUser.profile !== 'admin'}
												startAdornment={
													<InputAdornment position="start">
														<AdminPanelSettings fontSize="small" color="action" />
													</InputAdornment>
												}
											>
												<MenuItem value="admin">{i18n.t("userType.translate.admin")}</MenuItem>
												<MenuItem value="supervisor">{i18n.t("userType.translate.supervisor")}</MenuItem>
												<MenuItem value="user">{i18n.t("userType.translate.user")}</MenuItem>
											</Select>
										</FormControl>
									</Grid>

									<Grid item xs={12} md={6}>
										<TextField
											label="Cargo na empresa"
											placeholder="Opcional"
											value={position === 'null' ? null : position}
											onChange={(e) => setPosition(e.target.value)}
											variant="outlined"
											size="small"
											fullWidth
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<Work fontSize="small" color="action" />
													</InputAdornment>
												),
											}}
										/>
									</Grid>

									<Grid item xs={12}>
										<FormControl
											fullWidth
											variant="outlined"
											size="small"
										>
											<InputLabel id="period-selection-label">Períodos</InputLabel>
											<Select
												labelId="period-selection-label"
												id="period-selection"
												multiple
												value={periodWork}
												onChange={handleChangePeriodWork}
												input={<OutlinedInput label="Períodos" />}
												startAdornment={
													<InputAdornment position="start">
														<Schedule fontSize="small" color="action" />
													</InputAdornment>
												}
												renderValue={(selected) => (
													<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
														{translatePeriods(selected).map((value) => (
															<Chip key={value} label={value} size="small" />
														))}
													</Box>
												)}
												MenuProps={MenuProps}
											>
												{availablePeriods.map((p) => (
													<MenuItem key={p.key} value={p.key}>
														<Box sx={{ display: 'flex', alignItems: 'center' }}>
															<Schedule fontSize="small" sx={{ mr: 1, color: p.color }} />
															{p.label}
															{p.times && (
																<Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
																	({p.times})
																</Typography>
															)}
														</Box>
													</MenuItem>
												))}
											</Select>
											<FormHelperText>
												<Box sx={{ display: 'flex', alignItems: 'center' }}>
													<Info fontSize="small" sx={{ mr: 0.5, fontSize: '0.75rem' }} />
													Períodos de trabalho do usuário
												</Box>
											</FormHelperText>
										</FormControl>
									</Grid>

									{isUnidesk && (
										<Grid item xs={12}>
											<TextField
												label="Id Unidesk"
												value={unideskId}
												onChange={(e) => setUnideskId(e.target.value)}
												variant="outlined"
												size="small"
												fullWidth
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<Badge fontSize="small" color="action" />
														</InputAdornment>
													),
												}}
											/>
										</Grid>
									)}
								</Grid>
							</StyledPaper>
						</Grid>
					)}

					{/* Seção de Folgas */}
					{(loggedInUser.profile !== 'adminSuper' && loggedInUser.profile !== 'user') && (
						<Grid item xs={12}>
							<StyledPaper>
								<SectionHeader>
									<SectionIcon><EventBusy /></SectionIcon>
									<Typography variant="subtitle1" fontWeight="bold">
										Folgas
									</Typography>
								</SectionHeader>

								<Grid container spacing={2}>
									{/* Folgas fixas semanais */}
									<Grid item xs={12}>
										<FormControl fullWidth variant="outlined" size="small">
											<InputLabel id="daysoff-selection-label">Dias de folga fixos</InputLabel>
											<Select
												labelId="daysoff-selection-label"
												multiple
												value={selectedDaysOff}
												onChange={(e) => setSelectedDaysOff(
													typeof e.target.value === 'string'
														? e.target.value.split(',').map(Number)
														: e.target.value
												)}
												input={<OutlinedInput label="Dias de folga fixos" />}
												renderValue={(selected) => (
													<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
														{selected.map((val) => (
															<Chip
																key={val}
																label={weekDays.find(d => d.value === val)?.label}
																size="small"
															/>
														))}
													</Box>
												)}
												MenuProps={MenuProps}
											>
												{weekDays.map((day) => (
													<MenuItem key={day.value} value={day.value}>
														<Checkbox
															checked={selectedDaysOff.indexOf(day.value) > -1}
															size="small"
														/>
														{day.label}
													</MenuItem>
												))}
											</Select>
											<FormHelperText>
												<Box sx={{ display: 'flex', alignItems: 'center' }}>
													<Info fontSize="small" sx={{ mr: 0.5, fontSize: '0.75rem' }} />
													Dias da semana em que o usuário folga
												</Box>
											</FormHelperText>
										</FormControl>
									</Grid>

									{/* Folgas avulsas */}
									<Grid item xs={12}>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
											Folgas avulsas (datas específicas)
										</Typography>
										<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
											<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
												<DesktopDatePicker
													label="Adicionar data"
													value={extraDayInput}
													onChange={(newVal) => setExtraDayInput(newVal)}
													format="DD/MM/YYYY"
													slotProps={{
														textField: { size: 'small', fullWidth: true }
													}}
												/>
											</LocalizationProvider>
											<Button
												variant="outlined"
												size="small"
												onClick={() => handleAddExtraDay(extraDayInput)}
												disabled={!extraDayInput}
												sx={{ minWidth: 100, height: 40 }}
											>
												Adicionar
											</Button>
										</Box>
										{extraDaysOff.length > 0 && (
											<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
												{extraDaysOff.map((date) => (
													<Chip
														key={date}
														label={dayjs(date).format('DD/MM/YYYY')}
														onDelete={() => handleRemoveExtraDay(date)}
														size="small"
														color="primary"
														variant="outlined"
													/>
												))}
											</Box>
										)}
									</Grid>
								</Grid>
							</StyledPaper>
						</Grid>
					)}

					{/* Seção de Setores */}
					{(loggedInUser.profile !== 'adminSuper' && loggedInUser.profile !== 'user') && (
						<Grid item xs={12}>
							<StyledPaper>
								<SectionHeader>
									<SectionIcon><Business /></SectionIcon>
									<Typography variant="subtitle1" fontWeight="bold">
										Setores
									</Typography>
								</SectionHeader>

								<Box sx={{ mb: 2 }}>
									<QueueSelect
										selectedQueueIds={selectedQueueIds}
										onChange={values => setSelectedQueueIds(values)}
									/>
								</Box>

								<FormControlLabel
									control={
										<Checkbox
											checked={noQueue}
											onChange={() => setNoQueue(!noQueue)}
											size="small"
											color="primary"
										/>
									}
									label={
										<Box sx={{ display: 'flex', alignItems: 'center' }}>
											<CheckCircle fontSize="small" sx={{ mr: 0.5 }} />
											<Typography variant="body2">Exibir atendimentos sem setor</Typography>
										</Box>
									}
								/>
							</StyledPaper>
						</Grid>
					)}
				</Grid>
			</DialogContent>

			<Divider />

			<DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
				<Button
					onClick={handleClose}
					color="inherit"
					disabled={isSubmitting}
					variant="outlined"
					size="small"
					startIcon={isSubmitting ? null : <Close />}
				>
					{i18n.t("userModal.buttons.cancel")}
				</Button>

				<LoadingButton
					onClick={handleSaveUser}
					color="primary"
					disabled={isSubmitting}
					variant="contained"
					size="small"
					startIcon={isSubmitting ? null : <Save />}
				>
					{userId
						? i18n.t("userModal.buttons.okEdit")
						: i18n.t("userModal.buttons.okAdd")}
					{isSubmitting && <ButtonProgress size={20} />}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default CreateOrEditUserModal;